import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useGetSiteById } from "hooks/sites/useGetSiteById";
import { AppTitleContext } from "app_context";
import { styled } from "@mui/material/styles";
import { Link, useHistory } from "react-router-dom";
import { FleetCaseMeanResponseTimeKpi } from "components/OverviewPages/KPIs/CaseMeanTimeKpi";
import { FleetCaseSummaryKpi } from "components/OverviewPages/KPIs/CaseSummaryKpi";
import { FleetSavingsKpi } from "components/OverviewPages/KPIs/SavingsKpi";
import { MachinesDownKpi } from "components/OverviewPages/KPIs/MachinesDownKpi";
import { OverviewMapPanel } from "components/OverviewPages/Map/OverviewMapPanel";
import { Grid, Paper, Skeleton } from "@mui/material";
import { SiteTable } from "components/OverviewPages/SiteTable";
import { useFetchGet } from "hooks/fetch/useFetchGet";
import { Breadcrumbs } from "components/Breadcrumbs";
import { useTrackPageView } from "hooks/useTrackPageView";

export function SitePage() {
  useTrackPageView("Site");
  const [selectedMachine, setSelectedMachine] = useState(null);

  const appTitleContext = useContext(AppTitleContext);
  const history = useHistory();
  const { siteId: id } = useParams();
  const siteId = Number(id);

  const { loading: siteLoading, error: siteError, site } = useGetSiteById(
    siteId
  );

  const { loading: machinesLoading, response: machineData } = useFetchGet(
    `site_overview/${siteId}`
  );

  useEffect(() => {
    if (siteError) {
      history.replace("/notfound");
    }
  }, [siteError, history]);

  useEffect(() => {
    appTitleContext.setPageTitle(
      <Breadcrumbs>
        <Link to={`/pages/fleet`}>Fleet</Link>
        <div>{site?.name}</div>
      </Breadcrumbs>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  // Remap data for map component
  const mappedMapValues =
    machineData?.map(result => {
      return {
        latitude: result?.latitude,
        longitude: result?.longitude,
        name: result?.name,
        isMarkerSelected: (marker, selectedMarker) =>
          marker && selectedMarker && marker === selectedMarker,
        marker: result?.name
      };
    }) ?? [];

  const enableSelectedMachine = machineName => {
    setSelectedMachine(machineName);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={3}>
        <MachinesDownKpi expanded site={site} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <FleetCaseMeanResponseTimeKpi expanded site={site} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <FleetCaseSummaryKpi expanded site={site} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <FleetSavingsKpi expanded site={site} />
      </Grid>
      <Grid item xs={12} md={8}>
        <TableContainer elevation={0}>
          {machinesLoading || siteLoading ? (
            <FleetTableSkeletonWrapper>
              <FleetTableSkeleton />
            </FleetTableSkeletonWrapper>
          ) : (
            <SiteTable
              loading={machinesLoading || siteLoading}
              siteTableData={machineData ?? []}
              enableSelectedMachine={enableSelectedMachine}
              selectedMachineName={selectedMachine}
            />
          )}
        </TableContainer>
      </Grid>
      <Grid item xs={12} md={4}>
        {machinesLoading || siteLoading ? (
          <FleetMapSkeletonWrapper>
            <FleetMapSkeleton />
          </FleetMapSkeletonWrapper>
        ) : (
          <OverviewMapPanel
            data={mappedMapValues}
            selectedMarker={selectedMachine}
            onChangeMarker={enableSelectedMachine}
          />
        )}
      </Grid>
    </Grid>
  );
}

const TableContainer = styled(Paper)`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 8rem - 123px);
`;

const FleetTableSkeletonWrapper = styled("div")`
  display: flex;
  justify-content: center;
  height: 100%;
`;

const FleetTableSkeleton = styled(Skeleton)`
  transform: none;
  height: 50%;
  width: 97%;
  margin-top: 15px;
`;

const FleetMapSkeletonWrapper = styled("div")`
  height: 100%;
`;

const FleetMapSkeleton = styled(Skeleton)`
  transform: none;
  height: 100%;
`;
